import { ChangeDetectionStrategy, Component, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControl } from './base-control';

/**
 * On/off switch (design system Toggle, figma 297:3364): 64px track, radius 8
 * (square-ish, not a pill), knob is half the track. ControlValueAccessor over
 * a boolean.
 *
 *   <app-toggle [(ngModel)]="activateNow" />
 */
@Component({
  selector: 'app-toggle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Toggle), multi: true }],
  template: `
    <button
      type="button"
      role="switch"
      class="track"
      [class.on]="!!value()"
      [attr.aria-checked]="!!value()"
      [disabled]="disabled()"
      (click)="toggle()"
    ></button>
  `,
  styles: `
    /* Knob is "white-interactive": white in light, greyscale-700 in dark —
       no app utility, so a component-level var per the porting map. */
    .track {
      --knob-bg: var(--white);
      position: relative;
      display: inline-flex;
      width: 64px;
      height: 24px;
      flex: none;
      border: 0;
      border-radius: var(--radius-control);
      background: var(--surface-3);
      transition: background-color 0.15s ease;
    }
    :host-context(.dark) .track,
    :host-context([data-theme='dark']) .track {
      --knob-bg: var(--core-greyscale-700);
    }
    .track.on {
      background: var(--action-fill);
    }
    .track:disabled {
      opacity: 0.6;
    }
    .track.on:hover:not(:disabled) {
      background: var(--papercast-blue);
    }
    .track:not(.on):hover:not(:disabled) {
      background:
        linear-gradient(var(--overlay-down-1), var(--overlay-down-1)),
        linear-gradient(var(--surface-2), var(--surface-2));
    }

    /* Knob: half the track, full height, same 8px radius. */
    .track::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 32px;
      height: 100%;
      border-radius: var(--radius-control);
      background: var(--knob-bg);
      box-shadow: var(--shadow-toolbar);
      transition: transform 0.15s ease;
    }
    .track.on::after {
      transform: translateX(32px);
    }
    .track:hover:not(:disabled)::after {
      box-shadow:
        inset 0 0 0 1px var(--core-overlay-200),
        var(--shadow-toolbar);
    }
    .track:disabled::after {
      box-shadow: inset 0 0 0 1px var(--core-overlay-100);
    }
    .track.on:disabled::after {
      background: var(--surface-3);
    }
  `,
})
export class Toggle extends BaseControl<boolean> {
  protected toggle(): void {
    if (this.disabled()) return;
    this.setValue(!this.value());
    this.onTouched();
  }
}
